import React, { useEffect, useRef, useState } from 'react';
import './BlurText.css';

interface BlurTextProps {
  text?: string;
  delay?: number;
  className?: string;
  animateBy?: "words" | "letters";
  direction?: "top" | "bottom";
  threshold?: number;
  rootMargin?: string;
  easing?: (t: number) => number;
  onAnimationComplete?: () => void;
  style?: React.CSSProperties;
}

const BlurText: React.FC<BlurTextProps> = ({
  text = "",
  delay = 200,
  className = "",
  animateBy = "words",
  direction = "top",
  threshold = 0.1,
  rootMargin = "0px",
  onAnimationComplete,
  style
}) => {
  const elements = animateBy === "words" ? text.split(" ") : text.split("");
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [animatedElements, setAnimatedElements] = useState<number[]>([]);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(ref.current!);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  useEffect(() => {
    if (!inView) return;

    let timeout: NodeJS.Timeout;
    let count = 0;

    const animateNext = (index: number) => {
      timeout = setTimeout(() => {
        setAnimatedElements(prev => [...prev, index]);
        count++;
        
        if (index < elements.length - 1) {
          animateNext(index + 1);
        } else if (onAnimationComplete) {
          onAnimationComplete();
        }
      }, delay);
    };

    animateNext(0);

    return () => clearTimeout(timeout);
  }, [inView, elements.length, delay, onAnimationComplete]);

  return (
    <div 
      ref={ref} 
      className={`blur-text-container ${className}`}
      style={style}
    >
      {elements.map((element, index) => (
        <span
          key={index}
          className={`blur-text-element ${
            animatedElements.includes(index) 
              ? "blur-text-animated" 
              : direction === "top" 
                ? "blur-text-hidden-top" 
                : "blur-text-hidden-bottom"
          }`}
        >
          {element === " " ? "\u00A0" : element}
          {animateBy === "words" && index < elements.length - 1 && "\u00A0"}
        </span>
      ))}
    </div>
  );
};

export default BlurText;
